import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentSettingsOutput } from '../../../gamma-api';
import IbScrollSpy from '../common/IbScrollSpy';
import IbButton from '../common/IbButton';
import IbTypography from '../common/IbTypography';
import IbTextArea from '../common/IbTextArea';
import IbInput from '../common/IbInput';

import {
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  CONTENT_CLASS_NAME,
  FOOTER_CLASS_NAME,
  MAIN_CLASS_NAME,
} from './const';
import { DEFAULT_AGENT_SETTINGS } from './AgentGeneral';

const SUGGESTION_LINE_SEPARATOR = '\n';

export interface IAgentWidgetProps {
  agentSettings?: AgentSettingsOutput;
  onSave: (settings: AgentSettingsOutput) => Promise<void>;
}

const AgentWidget: React.FC<IAgentWidgetProps> = ({ agentSettings, onSave }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(agentSettings || DEFAULT_AGENT_SETTINGS);
  const [suggestions, setSuggestions] = useState<string[]>(agentSettings?.widget?.suggestions || []);
  const [displayName, setDisplayName] = useState(agentSettings?.widget?.display_name || '');
  const suggestionsValue = suggestions?.join(SUGGESTION_LINE_SEPARATOR);

  const onAgentSettingsPropChange = () => {
    setSettings(agentSettings || DEFAULT_AGENT_SETTINGS);

    if (!agentSettings) {
      return;
    }

    const suggestions = agentSettings.widget.suggestions || [];
    setSuggestions(suggestions);
  };
  useEffect(onAgentSettingsPropChange, [agentSettings]);

  const onSaveButtonClick = async () => {
    setSaving(true);
    const newSettings = {
      ...settings,
      widget: {
        ...settings.widget,
        display_name: displayName,
        suggestions,
      },
    };
    await onSave(newSettings);
    setSaving(false);
  };

  const onDisplayNameChange = (value: string) => setDisplayName(value);

  const onSuggestionsChange = (value: string) => setSuggestions(value.split(SUGGESTION_LINE_SEPARATOR));

  const renderDisplayNameBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Widget: Display name: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbInput value={displayName} onChange={onDisplayNameChange} />
      </div>
    </div>
  );

  const renderSuggestionsBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Agent: Widget: Suggestions: Title')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph type="secondary">{t('Agent: Widget: Suggestions: Description')}</IbTypography.Paragraph>
        <IbTextArea rows={10} value={suggestionsValue} onChange={onSuggestionsChange} />
      </div>
    </div>
  );

  const sections = [
    { id: 'displayName', title: t('Agent: Widget: Display name: Title'), content: renderDisplayNameBlock() },
    { id: 'suggestions', title: t('Agent: Widget: Suggestions: Title'), content: renderSuggestionsBlock() },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy displayNav sections={sections} />
      </div>
      <div className={FOOTER_CLASS_NAME}>
        <IbButton disabled={saving} onClick={onSaveButtonClick}>
          {t('Settings: Save')}
        </IbButton>
      </div>
    </div>
  );
};

export default AgentWidget;
